import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer } from "@/components/layout/PageLayout";
import OperationsProjectDetail from "@/pages/operations/OperationsProjectDetail";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * DEV-020 · Universal Single Project Focus View for Operations + Sales.
 *
 * A thin role-aware wrapper around the existing `OperationsProjectDetail`
 * component. Removes the surrounding master-detail list/filter chrome
 * so the user gets the full available width to work on ONE project
 * while keeping the correct role shell and every existing permission
 * check intact.
 *
 * Route params:
 *   /operations/projects/:projectId    Operations shell
 *   /sales/projects/:projectId         Sales route (still uses OperationsShell
 *                                       — Sales users share it via the
 *                                       SALES-ROLE-001B convention).
 *
 * Origin state:
 *   Back navigation reads `location.state.returnTo` (an encoded URL of
 *   the originating list) so filters / saved views / scroll are
 *   preserved. Direct/refresh loads fall back to a role-aware default
 *   ("Pipeline" for Sales, "Projects" for Operations). Browser Back
 *   continues to work natively.
 *
 * Authorisation:
 *   No new backend surface — reuses `api.getProject(id)`, which already
 *   enforces the canonical role/visibility rules (Ops sees all, Sales
 *   sees `_sales_visible_project_ids`, engineer sees assignments,
 *   customer sees own). Unauthorised ids surface a friendly error
 *   without disclosing project data (backend returns 404 already).
 */
export default function OperationsProjectFocus() {
    const { projectId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [urlParams] = useSearchParams();
    const { user } = useAuth();

    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    // DEV-019 UAT correction 2 · `?section=<key>` scrolls the matching
    // detail section into view once the project has loaded. Kept as a
    // URL query so refresh / share reproduces the behaviour and future
    // consumers can target any section by adding an id="section-<key>"
    // wrapper in `OperationsProjectDetail`.
    const focusSection = urlParams.get("section");
    // DEV-020 UAT correction 2 · The legacy master-detail flow passed
    // canonical task / survey drill-in context via `?taskId=` /
    // `?reviewSurvey=`. Focus mode preserves the same contract so
    // deep-links from Calendar (linked-task Open) and Notification
    // Centre continue to auto-open the target task/survey without
    // re-introducing the master-detail selector.
    const focusTaskId = urlParams.get("taskId");
    const reviewSurveyId = urlParams.get("reviewSurvey");
    const scrolledRef = useRef(false);

    const returnTo = location.state?.returnTo
        || ((user?.role || "").toLowerCase() === "sales"
            ? "/operations/projects"
            : "/operations/projects");
    const backLabel = (user?.role || "").toLowerCase() === "sales" ? "Back to Pipeline" : "Back to Projects";

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const p = await api.getProject(projectId);
            setProject(p);
        } catch (e) {
            const status = e?.response?.status;
            // 404 covers both "unknown" and "unauthorised" — never
            // leak project existence in the message.
            setErr(status === 404
                ? "This project isn't available for your account."
                : (formatApiError(e?.response?.data?.detail) || "Failed to load project"));
            setProject(null);
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    // DEV-019 UAT correction 2 · Scroll the requested section into view
    // AFTER the project has rendered — a single-shot effect keyed by
    // `focusSection` + project so a subsequent update doesn't yank the
    // page back if the user has scrolled elsewhere.
    useEffect(() => {
        if (!focusSection || !project || scrolledRef.current) return;
        // Defer to the next paint so the detail section has mounted.
        const raf = requestAnimationFrame(() => {
            const el = document.getElementById(`section-${focusSection}`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                scrolledRef.current = true;
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [focusSection, project]);

    const handleUpdated = (updated) => setProject((prev) => ({ ...prev, ...updated }));

    return (
        <OperationsShell>
            <PageContainer width="full" testid="ops-project-focus">
                <div className="flex items-center justify-between mb-4">
                    <Link
                        to={returnTo}
                        data-testid="ops-project-focus-back"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        <ArrowLeft size={14} strokeWidth={2} /> {backLabel}
                    </Link>
                    {project && (
                        <div className="text-[11px] tracking-[0.14em] uppercase text-[#4B5565] font-medium" data-testid="ops-project-focus-ref">
                            {project.reference}
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-[#4B5565] p-8" data-testid="ops-project-focus-loading">
                        <Loader2 className="animate-spin" size={16} /> Loading project…
                    </div>
                ) : err ? (
                    <div className="rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-5" data-testid="ops-project-focus-error">
                        <div className="text-sm font-medium text-[#131B2F]">{err}</div>
                        <button
                            type="button"
                            onClick={() => navigate(returnTo)}
                            data-testid="ops-project-focus-error-back"
                            className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7]"
                        >
                            <ArrowLeft size={12} /> {backLabel}
                        </button>
                    </div>
                ) : project ? (
                    <OperationsProjectDetail
                        project={project}
                        onProjectUpdated={handleUpdated}
                        focusTaskId={focusTaskId}
                        reviewSurveyId={reviewSurveyId}
                    />
                ) : null}
            </PageContainer>
        </OperationsShell>
    );
}
